import { toPrintableQrValue } from "@mvk/shared";
import { isAdminHost, isMaxMiniAppHost } from "../services/channelHosts";

/**
 * Visitor PWA origin for printable QR (phone camera → /go landing).
 * Never use admin or MAX mini-app host — /go lives on PUBLIC_WEB_URL.
 */
const DEV_PUBLIC_WEB = "https://mvk.dev.tezam.by";
const PROD_PUBLIC_WEB = "https://app.mvk.ru";

/** Map admin / MAX host → visitor PWA host for the same environment. */
export function visitorWebOriginFromHost(hostname: string): string | null {
  const host = hostname.toLowerCase();
  if (!host) return null;

  // Customer prod
  if (host === "admin.mvk.ru" || host === "max.mvk.ru" || host === "app.mvk.ru") {
    return PROD_PUBLIC_WEB;
  }
  if (host.endsWith(".mvk.ru") && (host.startsWith("admin.") || host.startsWith("max."))) {
    return `https://app.${host.split(".").slice(1).join(".")}`;
  }

  // Tezam / legacy hosts
  if (host.includes("mvk-admin.") || host.startsWith("mvk-max.") || host === "mvk.dev.tezam.by") {
    return DEV_PUBLIC_WEB;
  }
  if (host.includes("tezam.by") && host.includes("mvk")) {
    return DEV_PUBLIC_WEB;
  }

  return null;
}

function resolvePublicWebUrl(): string {
  const fromEnv = (import.meta.env.VITE_PUBLIC_WEB_URL as string | undefined)?.trim();
  if (fromEnv) return fromEnv.replace(/\/+$/, "");

  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (isAdminHost(host) || isMaxMiniAppHost(host)) {
      return visitorWebOriginFromHost(host) ?? PROD_PUBLIC_WEB;
    }
    if (host === "localhost" || host === "127.0.0.1") {
      return window.location.origin;
    }
    // Already on PWA / gateway host
    if (host.includes("mvk.") || host === "app.mvk.ru") {
      return window.location.origin;
    }
    const mapped = visitorWebOriginFromHost(host);
    if (mapped) return mapped;
  }

  // Prefer customer prod when env not baked (admin builds often omit VITE_PUBLIC_WEB_URL)
  return PROD_PUBLIC_WEB;
}

export const PUBLIC_WEB_URL = resolvePublicWebUrl();

export function printableQr(opaque: string): string {
  return toPrintableQrValue(opaque, PUBLIC_WEB_URL);
}
